import type { AuditEntry, AuditList } from "@divespots/contracts";
import { AuditList as AuditListSchema } from "@divespots/contracts";

/**
 * Fixtures for the admin action log: INVENTED people on reserved domains only (this file ships in
 * the production bundle). Covers every action and entity, a script row and a deleted account.
 */
const SPOT_A = "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c01"; // Enteneck
const SPOT_B = "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c02";
const SPOT_NEW = "7a2b3c4d-5e6f-4a1b-8c9d-0e1f2a3b4c09";
const SUG_1 = "5b1c0000-0000-4000-8000-000000000001";
const SUG_2 = "5b1c0000-0000-4000-8000-000000000002";
const SUG_3 = "5b1c0000-0000-4000-8000-000000000003";
const OBJ_1 = "0b1e0000-0000-4000-8000-000000000001";
const OBJ_2 = "0b1e0000-0000-4000-8000-000000000002";

const staff = {
  id: "e2e-u3",
  name: "Max Beispiel",
  email: "max.beispiel@example.com",
  role: "moderator" as const,
  label: null,
};
const admin = {
  id: "e2e-user",
  name: "E2E",
  email: "e2e@divespots.test",
  role: "admin" as const,
  label: null,
};
const diver = {
  id: "e2e-u2",
  name: "Anna Muster-Beispiel",
  email: "averyveryverylong.firstname.lastname@example-diving-club.test",
  role: "diver" as const,
  label: null,
};
const script = { id: null, name: null, email: null, role: null, label: "system:bathy-sync" };
const gone = { id: null, name: null, email: null, role: null, label: null };

type Row = Omit<AuditEntry, "id" | "suggestionId"> & { suggestionId?: string | null };
const tgt = (label: string | null, spotId: string | null, deleted = false) => ({
  label,
  spotId,
  deleted,
});

/** Newest first. Times are fixed so the list is stable. */
const HAND: Row[] = [
  {
    at: "2026-11-03T13:05:00.000Z",
    actor: admin,
    entity: "spot",
    entityId: SPOT_A,
    action: "update",
    target: tgt("Enteneck", SPOT_A),
    diff: {
      name: { from: "Enteneck", to: "Enteneck (Ost)" },
      status: { from: "draft", to: "published" },
      description: {
        from: { de: "Kurzer Einstieg am Ufer.", en: "" },
        to: {
          de: "Kurzer Einstieg am Ufer, danach flach abfallend bis etwa 12 m. Bei Westwind schlecht einsehbar, Parkplatz am Hafen.",
          en: "Short shore entry.",
        },
      },
    },
  },
  {
    at: "2026-11-03T12:40:00.000Z",
    actor: admin,
    entity: "suggestion",
    entityId: SUG_1,
    action: "approve",
    target: tgt("Neuer Platz am Steg", SPOT_NEW),
    suggestionId: SUG_1,
    diff: { status: { from: "pending", to: "approved" }, resultId: SPOT_NEW, adjusted: true },
  },
  {
    at: "2026-11-03T12:40:00.000Z",
    actor: admin,
    entity: "spot",
    entityId: SPOT_NEW,
    action: "create",
    target: tgt("Neuer Platz am Steg", SPOT_NEW),
    suggestionId: SUG_1,
    diff: { after: { name: "Neuer Platz am Steg", status: "published" }, suggestionId: SUG_1 },
  },
  {
    at: "2026-11-03T10:12:00.000Z",
    actor: staff,
    entity: "suggestion",
    entityId: SUG_2,
    action: "reject",
    target: tgt("Wrack Nord", SPOT_B),
    suggestionId: SUG_2,
    diff: { status: { from: "pending", to: "rejected" }, reason: "Doppelt vorhanden." },
  },
  {
    at: "2026-11-03T09:30:00.000Z",
    actor: diver,
    entity: "suggestion",
    entityId: SUG_3,
    action: "withdraw",
    target: tgt("Alter Anker", SPOT_A),
    diff: { status: { from: "pending", to: "withdrawn" } },
  },
  {
    at: "2026-11-02T18:20:00.000Z",
    actor: diver,
    entity: "suggestion",
    entityId: SUG_3,
    action: "submit",
    target: tgt("Alter Anker", SPOT_A),
    diff: { kind: "object_create", targetId: SPOT_A },
  },
  {
    at: "2026-11-02T17:00:00.000Z",
    actor: gone,
    entity: "suggestion",
    entityId: SUG_2,
    action: "submit",
    target: tgt("Wrack Nord", SPOT_B),
    diff: { kind: "spot_edit", targetId: SPOT_B, backfilled: true },
  },
  {
    at: "2026-11-02T16:45:00.000Z",
    actor: diver,
    entity: "sighting",
    entityId: "5e0a0000-0000-4000-8000-000000000001",
    action: "create",
    target: tgt("Alte Toilette", SPOT_A),
    diff: { objectId: OBJ_1, seenOn: "2026-11-02", present: true },
  },
  {
    at: "2026-11-02T16:50:00.000Z",
    actor: diver,
    entity: "sighting",
    entityId: "5e0a0000-0000-4000-8000-000000000001",
    action: "update",
    target: tgt("Alte Toilette", SPOT_A),
    diff: { present: { from: true, to: false } },
  },
  {
    at: "2026-11-02T14:10:00.000Z",
    actor: staff,
    entity: "object",
    entityId: OBJ_1,
    action: "update",
    target: tgt("Alte Toilette", SPOT_A),
    diff: {
      depthOverrideM: { from: null, to: 7.5 },
      model: { scale: { from: 1, to: 1.5 }, headingDeg: { from: 0, to: 90 } },
    },
  },
  {
    at: "2026-11-02T14:00:00.000Z",
    actor: staff,
    entity: "object",
    entityId: OBJ_2,
    action: "create",
    target: tgt("Holzboot", SPOT_A),
    diff: { after: { name: { de: "Holzboot" } } },
  },
  {
    at: "2026-11-02T09:00:00.000Z",
    actor: staff,
    entity: "object",
    entityId: OBJ_2,
    action: "delete",
    target: tgt("Holzboot", SPOT_A, true),
    diff: { before: { name: { de: "Holzboot" } } },
  },
  {
    at: "2026-11-01T22:30:00.000Z",
    actor: script,
    entity: "lake",
    entityId: "bodensee",
    action: "update",
    target: tgt("Bodensee", null),
    diff: { waterLevelM: { from: 395.2, to: 395.4 } },
  },
  {
    at: "2026-11-01T11:00:00.000Z",
    actor: admin,
    entity: "user",
    entityId: "e2e-u4",
    action: "update",
    target: tgt("Mia Keller", null),
    diff: { banned: { from: false, to: true }, password: "changed" },
  },
  {
    at: "2026-10-31T10:00:00.000Z",
    actor: admin,
    entity: "settings",
    entityId: "site",
    action: "update",
    target: tgt(null, null),
    diff: { registrationOpen: { from: false, to: true } },
  },
  {
    at: "2026-10-30T08:00:00.000Z",
    actor: gone,
    entity: "user",
    entityId: "e2e-gone",
    action: "delete",
    target: tgt(null, null, true),
    diff: { redacted: true },
  },
  {
    at: "2026-10-29T08:00:00.000Z",
    actor: admin,
    entity: "spot",
    entityId: SPOT_B,
    action: "delete",
    target: tgt("Sportplatz", SPOT_B, true),
    diff: { before: { name: "Sportplatz" } },
  },
];

/** Older filler so "Mehr laden" has something to load. */
const OLDER: Row[] = Array.from({ length: 20 }, (_, i) => ({
  at: new Date(Date.parse("2026-10-28T12:00:00.000Z") - i * 3_600_000 * 5).toISOString(),
  actor: i % 3 === 0 ? admin : staff,
  entity: "spot" as const,
  entityId: SPOT_A,
  action: "update" as const,
  target: tgt("Enteneck", SPOT_A),
  diff: { access: { from: `Variante ${i}`, to: `Variante ${i + 1}` } },
}));

export const auditFixtureEntries: AuditEntry[] = [...HAND, ...OLDER]
  .sort((a, b) => b.at.localeCompare(a.at))
  .map((r, i) => ({
    ...r,
    suggestionId: r.suggestionId ?? null,
    id: `a0d17000-0000-4000-8000-${String(1000 - i).padStart(12, "0")}`,
  }));

/** Filters, `limit` and the `c:<offset>` cursor, like the API (newest first). */
export function auditFixtureList(query: Record<string, string | undefined>): AuditList | null {
  const limit = Math.min(200, Math.max(1, Number(query.limit ?? 50) || 50));
  let offset = 0;
  if (query.before !== undefined) {
    const m = /^c:(\d{1,6})$/.exec(query.before);
    if (!m) return null;
    offset = Number(m[1]);
  }
  const rows = auditFixtureEntries.filter(
    (e) =>
      (!query.entity || e.entity === query.entity) &&
      (!query.action || e.action === query.action) &&
      (!query.actorId || e.actor.id === query.actorId) &&
      (!query.entityId || e.entityId === query.entityId) &&
      (!query.spotId || e.target.spotId === query.spotId),
  );
  const page = rows.slice(offset, offset + limit);
  const next = offset + limit < rows.length ? `c:${offset + limit}` : null;
  return AuditListSchema.parse({ entries: page, next });
}
